import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, Eye, EyeOff, Unlock, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Gate component for the /financials page.
 *
 * Behaviour (per user spec — "every time you get out that section,
 * coming back to it should ask again"):
 *   - We DON'T persist the unlocked state across navigation. The
 *     component remounts whenever the user lands on /financials, and
 *     it asks for the PIN again.
 *   - We don't keep PIN status in sessionStorage either, because
 *     sessionStorage survives tab navigation.
 *   - If the business has no PIN configured, we render children
 *     immediately.
 *
 * @param {object}   props
 * @param {string}   props.businessSlug
 * @param {ReactNode} props.children
 */
export default function FinancialsLock({ businessSlug, children }) {
  const [status, setStatus] = useState({ loading: true, hasPin: false });
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!businessSlug) return;
    let cancelled = false;
    axios
      .get(`${BASE_API}/b/${businessSlug}/financials/pin/status`)
      .then((res) => {
        if (cancelled) return;
        const hasPin = !!res.data.has_pin;
        const enabled = !!res.data.enabled;
        setStatus({ loading: false, hasPin });
        // No PIN configured OR protection toggled off → render through.
        if (!hasPin || !enabled) setUnlocked(true);
      })
      .catch(() => {
        if (cancelled) return;
        // If the status endpoint is unreachable, fail open (better to
        // show financials than wedge the page on a backend hiccup).
        setStatus({ loading: false, hasPin: false });
        setUnlocked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [businessSlug]);

  const submit = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!pin || pin.length < 4) {
      setErr("Enter your 4–8 digit PIN.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const r = await axios.post(`${BASE_API}/b/${businessSlug}/financials/pin/verify`, { pin });
      setUnlocked(true);
      toast.success(r?.data?.master ? "Unlocked (admin override)" : "Unlocked");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Wrong PIN";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }, [businessSlug, pin]);

  if (status.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }
  if (unlocked) return children;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4" data-testid="financials-lock-screen">
      <Card className="w-full max-w-sm bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-xl">
        <CardContent className="p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center mb-4 shadow-lg shadow-amber-500/20">
              <Lock className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Financials are locked</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Enter your PIN to see revenue, profit, and tax numbers.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <Input
                type={reveal ? "text" : "password"}
                inputMode="numeric"
                pattern="\d*"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="• • • •"
                autoFocus
                maxLength={8}
                className="text-center text-2xl tracking-[0.6em] font-mono pr-10"
                data-testid="financials-pin-input"
              />
              <button
                type="button"
                onClick={() => setReveal((r) => !r)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={reveal ? "Hide" : "Show"}
              >
                {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {err && (
              <p className="text-xs text-rose-500 text-center" data-testid="financials-pin-error">
                {err}
              </p>
            )}

            <Button
              type="submit"
              disabled={busy || pin.length < 4}
              className="w-full bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white font-semibold"
              data-testid="financials-pin-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlock className="w-4 h-4 mr-2" />}
              {busy ? "Checking…" : "Unlock"}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-center gap-2 text-[11px] text-slate-400">
            <ShieldCheck className="w-3 h-3" />
            Hidden so your team can't see the money
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
